import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Wallet,
  RefreshCw,
  Loader2,
  Plus,
  Minus,
  Gift,
  AlertTriangle,
  Info,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  ADJUSTMENT_OPTIONS,
  ADJUSTMENT_TYPES,
  canActorManageTargetWallet,
  canActorManageWallet,
  deriveCreditsFromDollarsInput,
  deriveDollarsDisplayFromCreditsInput,
  formatCredits,
  formatDollarsFromCents,
  formatDollarsFromCredits,
  isDebitAdjustment,
  validateAdjustmentForm,
} from '../../utils/walletConversion';

const QUICK_ACTIONS = [
  { key: ADJUSTMENT_TYPES.ADD_BALANCE, label: 'Add Balance', icon: Plus, variant: 'add' },
  { key: ADJUSTMENT_TYPES.DEBIT_BALANCE, label: 'Debit Balance', icon: Minus, variant: 'debit' },
  { key: ADJUSTMENT_TYPES.ADD_COMPLIMENTARY, label: 'Add Credits', icon: Gift, variant: 'add' },
  { key: ADJUSTMENT_TYPES.DEBIT_COMPLIMENTARY, label: 'Debit Credits', icon: Minus, variant: 'debit' },
];

function WalletSkeleton() {
  return (
    <div className="animate-pulse space-y-3">
      <div className="h-8 bg-slate-200 rounded-lg w-1/2" />
      <div className="h-4 bg-slate-100 rounded w-3/4" />
      <div className="h-4 bg-slate-100 rounded w-2/3" />
      <div className="h-10 bg-slate-100 rounded w-full" />
    </div>
  );
}

function formatTxnDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

function formatTxnAmount(txn) {
  const centsDelta = txn.amount_cents_delta || 0;
  const walletCreditsDelta = txn.wallet_credits_delta || 0;
  const complimentaryDelta = txn.complimentary_credits_delta || 0;

  if (walletCreditsDelta !== 0) {
    const sign = walletCreditsDelta > 0 ? '+' : '-';
    const dollars = formatDollarsFromCents(Math.abs(centsDelta));
    return `${sign}${dollars} / ${sign}${Math.abs(walletCreditsDelta).toLocaleString()} credits`;
  }
  if (complimentaryDelta !== 0) {
    const sign = complimentaryDelta > 0 ? '+' : '-';
    const dollars = formatDollarsFromCredits(Math.abs(complimentaryDelta));
    return `${sign}${Math.abs(complimentaryDelta).toLocaleString()} credits / ${sign}${dollars} value`;
  }
  return '—';
}

const EditUserPaymentsTab = ({
  user,
  agencyId,
  currentUser,
  apiBase,
  headers,
}) => {
  const [wallet, setWallet] = useState(null);
  const [walletLoading, setWalletLoading] = useState(true);
  const [walletError, setWalletError] = useState(null);

  const [transactions, setTransactions] = useState([]);
  const [txnTotal, setTxnTotal] = useState(0);
  const [txnLoading, setTxnLoading] = useState(true);
  const [txnError, setTxnError] = useState(null);
  const [txnSkip, setTxnSkip] = useState(0);
  const TXN_LIMIT = 25;

  const [adjustmentType, setAdjustmentType] = useState(ADJUSTMENT_TYPES.ADD_BALANCE);
  const [amountType, setAmountType] = useState('dollars');
  const [amountInput, setAmountInput] = useState('');
  const [reason, setReason] = useState('');
  const [referenceId, setReferenceId] = useState('');
  const [debitConfirmed, setDebitConfirmed] = useState(false);
  const [applying, setApplying] = useState(false);
  const [formError, setFormError] = useState(null);

  const canManage = useMemo(() => {
    if (!currentUser || !user) return false;
    if (!canActorManageWallet(currentUser.role, currentUser.agency_id, agencyId)) return false;
    return canActorManageTargetWallet(user.role, currentUser.role);
  }, [currentUser, user, agencyId]);

  const walletEndpoints = useMemo(() => {
    if (!user?.user_id || !apiBase) return null;
    return {
      wallet: `${apiBase}/${user.user_id}/wallet`,
      adjust: `${apiBase}/${user.user_id}/wallet/adjust`,
      transactions: `${apiBase}/${user.user_id}/wallet/transactions`,
    };
  }, [user?.user_id, apiBase]);

  const fetchWallet = useCallback(async () => {
    if (!walletEndpoints) return;
    setWalletLoading(true);
    setWalletError(null);
    try {
      const res = await axios.get(walletEndpoints.wallet, { headers });
      setWallet(res.data);
    } catch (err) {
      setWalletError(err.response?.data?.detail || 'Failed to load wallet');
      setWallet(null);
    } finally {
      setWalletLoading(false);
    }
  }, [walletEndpoints, headers]);

  const fetchTransactions = useCallback(
    async (skip = 0, append = false) => {
      if (!walletEndpoints) return;
      setTxnLoading(true);
      setTxnError(null);
      try {
        const res = await axios.get(walletEndpoints.transactions, {
          headers,
          params: { limit: TXN_LIMIT, skip },
        });
        const items = res.data.items || [];
        setTransactions((prev) => (append ? [...prev, ...items] : items));
        setTxnTotal(res.data.total || 0);
        setTxnSkip(skip);
      } catch (err) {
        setTxnError(err.response?.data?.detail || 'Failed to load transactions');
        if (!append) setTransactions([]);
      } finally {
        setTxnLoading(false);
      }
    },
    [walletEndpoints, headers]
  );

  useEffect(() => {
    if (canManage && walletEndpoints) {
      fetchWallet();
      fetchTransactions(0, false);
    }
  }, [canManage, walletEndpoints, fetchWallet, fetchTransactions]);

  const resetForm = () => {
    setAdjustmentType(ADJUSTMENT_TYPES.ADD_BALANCE);
    setAmountType('dollars');
    setAmountInput('');
    setReason('');
    setReferenceId('');
    setDebitConfirmed(false);
    setFormError(null);
  };

  const validation = useMemo(
    () =>
      validateAdjustmentForm({
        adjustmentType,
        amountType,
        amountInput,
        reason,
        debitConfirmed,
        wallet,
      }),
    [adjustmentType, amountType, amountInput, reason, debitConfirmed, wallet]
  );

  const conversionHint = useMemo(() => {
    if (!amountInput?.trim()) return null;
    if (amountType === 'dollars') {
      const credits = deriveCreditsFromDollarsInput(amountInput);
      return credits != null ? `= ${credits.toLocaleString()} credits` : null;
    }
    const dollars = deriveDollarsDisplayFromCreditsInput(amountInput);
    return dollars ? `= ${dollars} value` : null;
  }, [amountType, amountInput]);

  const handleApply = async () => {
    if (!walletEndpoints || !validation.valid) return;
    setApplying(true);
    setFormError(null);
    try {
      const payload = {
        adjustment_type: adjustmentType,
        amount_type: amountType,
        reason: reason.trim(),
        reference_id: referenceId.trim() || undefined,
      };
      if (amountType === 'dollars') {
        payload.amount_cents = validation.amountCents;
      } else {
        payload.credits = validation.credits;
      }

      await axios.post(walletEndpoints.adjust, payload, { headers });
      toast.success('Wallet adjustment applied successfully.');
      resetForm();
      await Promise.all([fetchWallet(), fetchTransactions(0, false)]);
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to apply adjustment';
      setFormError(message);
      toast.error(message);
    } finally {
      setApplying(false);
    }
  };

  if (!canManage) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-8 text-center text-slate-600">
        You do not have permission to manage payments for this user.
      </div>
    );
  }

  const totalDisplayCredits = wallet?.total_credits ?? 0;
  const totalDisplayDollars = formatDollarsFromCredits(totalDisplayCredits);

  return (
    <div className="space-y-6" data-testid="payments-tab">
      <div>
        <h3 className="text-xl font-semibold text-slate-900">Payments</h3>
        <p className="text-sm text-slate-500 mt-1">
          Manage this user&apos;s wallet balance, complimentary credits, and payment adjustments.
        </p>
        <p className="text-xs text-slate-400 mt-2">
          {user?.name || 'User'} · {user?.email}
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50/80">
              <div className="flex items-center gap-2">
                <Wallet className="text-emerald-600" size={18} />
                <h4 className="font-semibold text-slate-900">Wallet Summary</h4>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={fetchWallet}
                disabled={walletLoading}
                aria-label="Refresh wallet"
              >
                <RefreshCw size={16} className={walletLoading ? 'animate-spin' : ''} />
              </Button>
            </div>
            <div className="p-5">
              {walletLoading && !wallet ? (
                <WalletSkeleton />
              ) : walletError ? (
                <div className="text-center py-6">
                  <p className="text-sm text-red-600 mb-3">{walletError}</p>
                  <Button variant="outline" size="sm" onClick={fetchWallet}>
                    Retry
                  </Button>
                </div>
              ) : (
                <div className="space-y-5">
                  <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4">
                    <p className="text-xs font-medium uppercase tracking-wide text-emerald-700/80">
                      Total Available
                    </p>
                    <p className="text-3xl font-bold text-emerald-700 mt-1">
                      {formatCredits(totalDisplayCredits)}
                    </p>
                    <p className="text-sm text-emerald-600 mt-1">{totalDisplayDollars} value</p>
                  </div>

                  <div className="space-y-3 text-sm">
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-slate-500">Wallet Balance</span>
                      <span className="font-medium text-slate-900 text-right">
                        {formatDollarsFromCents(wallet?.balance_cents || 0)} /{' '}
                        {formatCredits(wallet?.wallet_credits || 0)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-slate-500">Complimentary Credits</span>
                      <span className="font-medium text-slate-900 text-right">
                        {formatCredits(wallet?.complimentary_credits || 0)} /{' '}
                        {formatDollarsFromCredits(wallet?.complimentary_credits || 0)} value
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {QUICK_ACTIONS.map((action) => {
              const Icon = action.icon;
              const isAdd = action.variant === 'add';
              return (
                <Button
                  key={action.key}
                  type="button"
                  variant="outline"
                  className={`h-auto py-3 justify-start ${
                    isAdd
                      ? 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'
                      : 'border-red-200 text-red-700 hover:bg-red-50'
                  }`}
                  onClick={() => {
                    setAdjustmentType(action.key);
                    setDebitConfirmed(false);
                  }}
                  data-testid={`quick-action-${action.key}`}
                >
                  <Icon size={16} className="mr-2 shrink-0" />
                  {action.label}
                </Button>
              );
            })}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5 space-y-4">
            <h4 className="font-semibold text-slate-900">Apply Adjustment</h4>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Adjustment Type</label>
              <Select value={adjustmentType} onValueChange={(v) => { setAdjustmentType(v); setDebitConfirmed(false); }}>
                <SelectTrigger className="h-11 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ADJUSTMENT_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Amount Type</label>
              <div className="inline-flex p-1 rounded-full border bg-slate-100 border-slate-200">
                {['dollars', 'credits'].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setAmountType(type)}
                    className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${
                      amountType === type
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {type === 'dollars' ? 'Dollars' : 'Credits'}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Amount</label>
              <Input
                value={amountInput}
                onChange={(e) => setAmountInput(e.target.value)}
                placeholder={amountType === 'dollars' ? '$0.00' : '1000'}
                className="h-11 rounded-xl"
                data-testid="adjustment-amount-input"
              />
              {conversionHint && (
                <p className="text-sm text-slate-500 mt-1.5">{conversionHint}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Reason / Internal Note</label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Example: Manual credit for support issue, refund correction, goodwill credit..."
                className="rounded-xl min-h-[88px]"
                data-testid="adjustment-reason-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Reference ID</label>
              <Input
                value={referenceId}
                onChange={(e) => setReferenceId(e.target.value)}
                placeholder="Optional invoice, Stripe, PostGrid, or internal reference"
                className="h-11 rounded-xl"
              />
            </div>

            {isDebitAdjustment(adjustmentType) && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
                <div className="flex items-start gap-2 text-sm text-amber-800">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                  <p>This will reduce the user&apos;s available balance. Confirm before applying.</p>
                </div>
                <label className="flex items-start gap-2 text-sm text-amber-900 cursor-pointer">
                  <Checkbox
                    checked={debitConfirmed}
                    onCheckedChange={(checked) => setDebitConfirmed(Boolean(checked))}
                    data-testid="debit-confirm-checkbox"
                  />
                  <span>I understand this adjustment will debit the user account.</span>
                </label>
              </div>
            )}

            {validation.errors.length > 0 && (amountInput || reason) && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 space-y-1">
                {validation.errors.map((msg) => (
                  <p key={msg}>{msg}</p>
                ))}
              </div>
            )}

            {formError && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {formError}
              </div>
            )}

            <div className="flex flex-wrap gap-3 pt-1">
              <Button
                type="button"
                onClick={handleApply}
                disabled={!validation.valid || applying}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="apply-adjustment-btn"
              >
                {applying ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Applying...
                  </>
                ) : (
                  'Apply Adjustment'
                )}
              </Button>
              <Button type="button" variant="outline" onClick={resetForm} disabled={applying}>
                Reset
              </Button>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/80">
              <h4 className="font-semibold text-slate-900">Transaction History</h4>
            </div>
            <div className="overflow-x-auto">
              {txnLoading && transactions.length === 0 ? (
                <div className="p-5">
                  <WalletSkeleton />
                </div>
              ) : txnError ? (
                <div className="p-8 text-center">
                  <p className="text-sm text-red-600 mb-3">{txnError}</p>
                  <Button variant="outline" size="sm" onClick={() => fetchTransactions(0, false)}>
                    Retry
                  </Button>
                </div>
              ) : transactions.length === 0 ? (
                <div className="p-10 text-center text-slate-500 text-sm">
                  No payment adjustments yet.
                </div>
              ) : (
                <table className="w-full min-w-[640px] text-sm">
                  <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Date</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Type</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Amount</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Reason</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Performed By</th>
                      <th className="text-right px-4 py-3 text-xs font-medium text-slate-500 uppercase">Balance After</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {transactions.map((txn) => {
                      const delta =
                        (txn.wallet_credits_delta || 0) + (txn.complimentary_credits_delta || 0);
                      const positive = delta > 0;
                      const negative = delta < 0;
                      return (
                        <tr key={txn.transaction_id} className="hover:bg-slate-50/80">
                          <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                            {formatTxnDate(txn.created_at)}
                          </td>
                          <td className="px-4 py-3 text-slate-700">{txn.display_type || txn.adjustment_type}</td>
                          <td
                            className={`px-4 py-3 font-medium whitespace-nowrap ${
                              positive ? 'text-emerald-600' : negative ? 'text-red-600' : 'text-slate-600'
                            }`}
                          >
                            {formatTxnAmount(txn)}
                          </td>
                          <td className="px-4 py-3 text-slate-600 max-w-[160px] truncate" title={txn.reason}>
                            {txn.reason}
                          </td>
                          <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                            {(txn.performed_by_name || txn.performed_by_user_id || '—').slice(0, 12)}
                          </td>
                          <td className="px-4 py-3 text-right text-slate-700 whitespace-nowrap">
                            {formatCredits(txn.total_credits_after || 0)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            {transactions.length < txnTotal && (
              <div className="p-4 border-t border-slate-100 text-center">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={txnLoading}
                  onClick={() => fetchTransactions(txnSkip + TXN_LIMIT, true)}
                >
                  {txnLoading ? 'Loading...' : 'Load more'}
                </Button>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 flex gap-2 text-sm text-slate-600">
            <Info size={16} className="shrink-0 mt-0.5 text-slate-400" />
            <p>
              All balance and credit adjustments are recorded for audit purposes. Passwords and payment
              processor secrets are never displayed here.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditUserPaymentsTab;
